/**
 * Issue & Transfer Management Page
 * AS9100D-Compliant Stock Issue & Transfer Workflow
 * 
 * Features:
 * - Create and manage issue orders with FIFO enforcement
 * - FIFO/FEFO pick suggestions
 * - Deviation handling with mandatory reasons
 * - Transfer requests between locations
 * - FOD verification checkpoint
 * 
 * AS9100D Clauses:
 * - 8.5.1: Production Control - Accountability
 * - 8.5.4: Preservation - FIFO/Stock Rotation
 * - 8.6: Release of Products
 * - 8.1: FOD Prevention
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Package,
  Plus,
  Search,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ArrowRightLeft,
  ArrowUpFromLine,
  MapPin,
  Box,
  AlertCircle,
  Check,
  X,
  Calendar,
  Hash,
  ListOrdered,
  ChevronRight,
  Info,
  Boxes,
  RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { formatIST } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';
import InventoryLayout from '../components/layouts/InventoryLayout';

// Status badge colors
const ISSUE_STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700 border-gray-200',
  PICKING: 'bg-blue-100 text-blue-700 border-blue-200',
  PICKED: 'bg-amber-100 text-amber-700 border-amber-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
};

const TRANSFER_STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700 border-gray-200',
  PENDING: 'bg-blue-100 text-blue-700 border-blue-200',
  IN_TRANSIT: 'bg-amber-100 text-amber-700 border-amber-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200',
};

const PRIORITY_COLORS = {
  LOW: 'bg-gray-100 text-gray-600',
  NORMAL: 'bg-blue-100 text-blue-600',
  HIGH: 'bg-amber-100 text-amber-600',
  URGENT: 'bg-red-100 text-red-600',
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ status, type = 'issue' }) => {
  const colors = type === 'issue' ? ISSUE_STATUS_COLORS : TRANSFER_STATUS_COLORS;
  return (
    <Badge variant="outline" className={`${colors[status] || colors.DRAFT} font-medium`}>
      {status?.replace(/_/g, ' ')}
    </Badge>
  );
};

/**
 * Priority Badge Component
 */
const PriorityBadge = ({ priority }) => (
  <Badge variant="outline" className={`${PRIORITY_COLORS[priority] || PRIORITY_COLORS.NORMAL} font-medium`}>
    {priority}
  </Badge>
);

/**
 * FIFO Indicator Component
 * Shows FIFO rank and compliance status
 */
const FIFOIndicator = ({ rank, isCompliant, daysOld }) => {
  if (!rank) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
            isCompliant 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-amber-100 text-amber-700 border border-amber-200'
          }`}>
            <ListOrdered className="w-3 h-3" />
            <span>#{rank}</span>
            {!isCompliant && <AlertTriangle className="w-3 h-3" />}
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="text-sm">
            <p className="font-medium">FIFO Rank #{rank}</p>
            {daysOld !== null && daysOld !== undefined && (
              <p className="text-gray-500">{daysOld} days since receipt</p>
            )}
            <p className="text-xs mt-1 text-gray-400">
              AS9100D 8.5.4: Stock rotation (FIFO/FEFO)
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Compliance Info Tooltip
 */
const ComplianceTooltip = ({ clause, title, description }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="w-4 h-4 text-blue-500 cursor-help" />
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="text-sm">
          <p className="font-medium text-blue-600">AS9100D {clause}</p>
          <p className="font-medium">{title}</p>
          <p className="text-gray-500 mt-1">{description}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/**
 * Create Issue Dialog
 */
const CreateIssueDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    demand_type: 'WORK_ORDER',
    demand_ref_number: '',
    destination_type: 'PRODUCTION',
    destination_description: '',
    priority: 'NORMAL',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/api/v1/inventory/v2/issues', formData);
      toast.success(`Issue order ${response.data.issue_number} created`);
      onSuccess(response.data);
      onOpenChange(false);
      setFormData({
        demand_type: 'WORK_ORDER',
        demand_ref_number: '',
        destination_type: 'PRODUCTION',
        destination_description: '',
        priority: 'NORMAL',
        notes: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create issue order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpFromLine className="w-5 h-5" />
            Create Issue Order
          </DialogTitle>
          <DialogDescription>
            Create a new stock issue request. FIFO-compliant picks will be suggested.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Demand Type
                <ComplianceTooltip
                  clause="8.5.1"
                  title="Production Control"
                  description="Track the source of demand for full accountability"
                />
              </Label>
              <Select
                value={formData.demand_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, demand_type: v }))}
              >
                <SelectTrigger data-testid="demand-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WORK_ORDER">Work Order</SelectItem>
                  <SelectItem value="TRANSFER_REQUEST">Transfer Request</SelectItem>
                  <SelectItem value="MISC">Miscellaneous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Reference Number</Label>
              <Input
                placeholder="e.g., WO-2026-0001"
                value={formData.demand_ref_number}
                onChange={(e) => setFormData(prev => ({ ...prev, demand_ref_number: e.target.value }))}
                data-testid="demand-ref-input"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Destination Type</Label>
              <Select
                value={formData.destination_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, destination_type: v }))}
              >
                <SelectTrigger data-testid="destination-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRODUCTION">Production</SelectItem>
                  <SelectItem value="SHIPPING">Shipping</SelectItem>
                  <SelectItem value="TRANSFER">Internal Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger data-testid="priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Destination Description</Label>
            <Input
              placeholder="e.g., Assembly Line 1"
              value={formData.destination_description}
              onChange={(e) => setFormData(prev => ({ ...prev, destination_description: e.target.value }))}
              data-testid="destination-desc-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              placeholder="Additional notes..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              data-testid="issue-notes-input"
            />
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="create-issue-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Issue Order
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Add Issue Line Dialog
 */
const AddIssueLineDialog = ({ open, onOpenChange, issueId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [partSearch, setPartSearch] = useState('');
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [quantity, setQuantity] = useState('');

  const searchParts = useCallback(async (search) => {
    if (!search || search.length < 2) {
      setParts([]);
      return;
    }
    
    try {
      const response = await api.get('/api/v1/parts/search', {
        params: { query: search, limit: 10 }
      });
      // API returns {items: [...]} structure
      setParts(response.data?.items || response.data || []);
    } catch (error) {
      console.error('Error searching parts:', error);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchParts(partSearch), 300);
    return () => clearTimeout(timer);
  }, [partSearch, searchParts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPart || !quantity) {
      toast.error('Please select a part and enter quantity');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/api/v1/inventory/v2/issues/${issueId}/lines`, {
        part_id: selectedPart.hpn_compact,
        quantity_requested: parseFloat(quantity),
      });
      toast.success('Line added successfully');
      onSuccess();
      onOpenChange(false);
      setSelectedPart(null);
      setQuantity('');
      setPartSearch('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add line');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Issue Line</DialogTitle>
          <DialogDescription>
            Add a part to issue. FIFO suggestions will be generated when you pick.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Part Number
              <ComplianceTooltip
                clause="8.5.2"
                title="Identification"
                description="Unique part identification for traceability"
              />
            </Label>
            <Input
              placeholder="Search by part number..."
              value={partSearch}
              onChange={(e) => {
                setPartSearch(e.target.value);
                setSelectedPart(null);
              }}
              data-testid="part-search-input"
            />
            
            {parts.length > 0 && !selectedPart && (
              <div className="border rounded-md max-h-40 overflow-y-auto">
                {parts.map((part) => (
                  <button
                    key={part.hpn_compact}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-gray-100 flex justify-between items-center"
                    data-testid={`part-option-${part.hpn_compact}`}
                    onClick={() => {
                      setSelectedPart(part);
                      setPartSearch(part.hpn_display);
                    }}
                  >
                    <div>
                      <p className="font-medium">{part.hpn_display}</p>
                      <p className="text-sm text-gray-500">{part.description}</p>
                    </div>
                    <span className="text-xs text-gray-400">{part.uom}</span>
                  </button>
                ))}
              </div>
            )}
            
            {selectedPart && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-green-800">{selectedPart.hpn_display}</p>
                      <p className="text-sm text-green-600">{selectedPart.description}</p>
                    </div>
                    <Badge variant="outline" className="bg-green-100">
                      {selectedPart.uom || 'EA'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          <div className="space-y-2">
            <Label>Quantity Requested</Label>
            <Input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Enter quantity"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="quantity-input"
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedPart} data-testid="add-line-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Line
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Pick Suggestions Dialog
 * Shows FIFO-compliant pick suggestions for an issue line
 */
const PickSuggestionsDialog = ({ open, onOpenChange, issue, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pickList, setPickList] = useState(null);
  const [picks, setPicks] = useState({});
  const [deviationReasons, setDeviationReasons] = useState({});

  const generatePickList = async () => {
    setGenerating(true);
    try {
      const response = await api.get(`/api/v1/inventory/v2/issues/${issue.issue_id}/pick-suggestions`);
      setPickList(response.data);
      
      // Initialize picks with suggested quantities
      const initialPicks = {};
      response.data.pick_list?.forEach(line => {
        initialPicks[line.line_id] = line.suggestions?.map(s => ({
          lot_id: s.lot_id,
          location_id: s.location_id,
          quantity_picked: s.suggested_pick_quantity,
          is_fifo_compliant: s.is_fifo_compliant,
        })) || [];
      });
      setPicks(initialPicks);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate pick list');
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (open && issue) {
      generatePickList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, issue]);

  const handleConfirmPicks = async (lineId) => {
    const linePicks = picks[lineId];
    if (!linePicks || linePicks.length === 0) {
      toast.error('No picks to confirm');
      return;
    }

    // Check if any non-FIFO picks need reasons
    for (const pick of linePicks) {
      if (!pick.is_fifo_compliant && pick.quantity_picked > 0) {
        const reason = deviationReasons[`${lineId}_${pick.lot_id}`];
        if (!reason) {
          toast.error('FIFO deviation reason required for non-compliant picks (AS9100D 8.5.4)');
          return;
        }
        pick.fifo_deviation_reason = reason;
      }
    }

    setLoading(true);
    try {
      await api.post(`/api/v1/inventory/v2/issues/${issue.issue_id}/lines/${lineId}/confirm-picks`, {
        picks: linePicks.filter(p => p.quantity_picked > 0)
      });
      toast.success('Picks confirmed');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm picks');
    } finally {
      setLoading(false);
    }
  };

  const updatePickQuantity = (lineId, lotId, quantity) => {
    setPicks(prev => ({
      ...prev,
      [lineId]: prev[lineId]?.map(p => 
        p.lot_id === lotId ? { ...p, quantity_picked: parseFloat(quantity) || 0 } : p
      ) || []
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered className="w-5 h-5" />
            FIFO Pick Suggestions
            <ComplianceTooltip
              clause="8.5.4"
              title="Preservation - Stock Rotation"
              description="Oldest stock (FIFO) or earliest expiry (FEFO) should be picked first"
            />
          </DialogTitle>
          <DialogDescription>
            Issue: {issue?.issue_number} - Pick suggestions follow FIFO/FEFO order
          </DialogDescription>
        </DialogHeader>
        
        {generating ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Generating FIFO suggestions...</span>
          </div>
        ) : pickList ? (
          <div className="space-y-6">
            {/* Summary */}
            <Card className={pickList.all_stock_available ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  {pickList.all_stock_available ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-green-700 font-medium">All requested stock is available</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <span className="text-amber-700 font-medium">
                        Some items have insufficient stock ({pickList.insufficient_stock?.length} items short)
                      </span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Pick list by line */}
            {pickList.pick_list?.map((line) => (
              <Card key={line.line_id} className="border-gray-200">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">
                        Line {line.line_number}: {line.part_number}
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        Requested: {line.quantity_requested} | 
                        Suggested: {line.quantity_suggested}
                        {line.use_fefo && (
                          <Badge variant="outline" className="ml-2 text-xs">FEFO</Badge>
                        )}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleConfirmPicks(line.line_id)}
                      disabled={loading}
                      data-testid={`confirm-picks-${line.line_number}`}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Picks'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">FIFO</TableHead>
                        <TableHead>Lot Number</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead className="text-right">Available</TableHead>
                        <TableHead className="text-right">Pick Qty</TableHead>
                        <TableHead>Days Old</TableHead>
                        <TableHead>Expiry</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {line.suggestions?.map((suggestion, idx) => (
                        <React.Fragment key={suggestion.lot_id}>
                          <TableRow className={idx === 0 ? 'bg-green-50' : ''}>
                            <TableCell>
                              <FIFOIndicator 
                                rank={suggestion.fifo_rank}
                                isCompliant={idx === 0 || picks[line.line_id]?.find(p => p.lot_id === suggestion.lot_id)?.quantity_picked === 0}
                                daysOld={suggestion.days_since_receipt}
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{suggestion.lot_number}</p>
                                {suggestion.supplier_lot_number && (
                                  <p className="text-xs text-gray-500">Supplier: {suggestion.supplier_lot_number}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-mono text-sm">{suggestion.location_code}</p>
                                {suggestion.bln_code && (
                                  <p className="text-xs text-gray-400">{suggestion.bln_code}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {suggestion.available_quantity}
                            </TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                min="0"
                                max={suggestion.available_quantity}
                                step="0.01"
                                className="w-24 text-right"
                                value={picks[line.line_id]?.find(p => p.lot_id === suggestion.lot_id)?.quantity_picked ?? suggestion.suggested_pick_quantity}
                                onChange={(e) => updatePickQuantity(line.line_id, suggestion.lot_id, e.target.value)}
                                data-testid={`pick-qty-${suggestion.lot_number}`}
                              />
                            </TableCell>
                            <TableCell>
                              {suggestion.days_since_receipt !== null && (
                                <span className={`text-sm ${suggestion.days_since_receipt > 90 ? 'text-amber-600' : 'text-gray-500'}`}>
                                  {suggestion.days_since_receipt}d
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {suggestion.days_to_expiry !== null && (
                                <span className={`text-sm ${suggestion.days_to_expiry <= 30 ? 'text-red-600' : suggestion.days_to_expiry <= 60 ? 'text-amber-600' : 'text-gray-500'}`}>
                                  {suggestion.days_to_expiry}d
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                          {/* FIFO Deviation Reason Input */}
                          {idx > 0 && picks[line.line_id]?.find(p => p.lot_id === suggestion.lot_id)?.quantity_picked > 0 && (
                            <TableRow className="bg-amber-50">
                              <TableCell colSpan={7}>
                                <div className="flex items-center gap-2 text-sm">
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                  <span className="text-amber-700">FIFO Deviation - Reason required (AS9100D 8.5.4):</span>
                                  <Input
                                    placeholder="Enter reason for not picking oldest lot first..."
                                    className="flex-1"
                                    value={deviationReasons[`${line.line_id}_${suggestion.lot_id}`] || ''}
                                    onChange={(e) => setDeviationReasons(prev => ({
                                      ...prev,
                                      [`${line.line_id}_${suggestion.lot_id}`]: e.target.value
                                    }))}
                                    data-testid={`deviation-reason-${suggestion.lot_number}`}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : null}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Complete Issue Dialog
 */
const CompleteIssueDialog = ({ open, onOpenChange, issue, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fodVerified, setFodVerified] = useState(false);

  const handleComplete = async () => {
    if (!fodVerified) {
      toast.error('FOD verification is required (AS9100D 8.1)');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/api/v1/inventory/v2/issues/${issue.issue_id}/complete`, {
        fod_verified: true
      });
      toast.success('Issue order completed');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete issue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Complete Issue Order
          </DialogTitle>
          <DialogDescription>
            Finalize issue {issue?.issue_number} and deduct from inventory
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Issue Summary */}
          <Card className="bg-gray-50">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Total Picked:</span>
                  <span className="ml-2 font-medium">{issue?.total_quantity_picked || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Lines:</span>
                  <span className="ml-2 font-medium">{issue?.line_count || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">FIFO Compliant:</span>
                  <span className={`ml-2 font-medium ${issue?.fifo_compliant ? 'text-green-600' : 'text-amber-600'}`}>
                    {issue?.fifo_compliant ? 'Yes' : 'No'}
                  </span>
                </div>
                {issue?.fifo_deviations_count > 0 && (
                  <div>
                    <span className="text-gray-500">Deviations:</span>
                    <span className="ml-2 font-medium text-amber-600">{issue?.fifo_deviations_count}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* FOD Verification */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="fod-verify"
                  checked={fodVerified}
                  onCheckedChange={setFodVerified}
                  data-testid="fod-verify-checkbox"
                />
                <div>
                  <Label htmlFor="fod-verify" className="flex items-center gap-2 cursor-pointer">
                    FOD Verification Complete
                    <ComplianceTooltip
                      clause="8.1"
                      title="FOD Prevention"
                      description="Verify no Foreign Object Debris before releasing material"
                    />
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    I confirm that FOD verification has been performed per AS9100D 8.1
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleComplete}
            disabled={loading || !fodVerified}
            className="bg-green-600 hover:bg-green-700"
            data-testid="complete-issue-btn"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Complete Issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Create Transfer Dialog
 */
const CreateTransferDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    from_location_id: '',
    to_location_id: '',
    transfer_type: 'INTERNAL',
    priority: 'NORMAL',
    reason: '',
    notes: '',
  });

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get('/api/v1/inventory/v2/locations');
        setLocations(response.data || []);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    if (open) fetchLocations();
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.from_location_id || !formData.to_location_id) {
      toast.error('Please select both source and destination locations');
      return;
    }
    
    if (formData.from_location_id === formData.to_location_id) {
      toast.error('Source and destination must be different');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/api/v1/inventory/v2/transfer-requests', formData);
      toast.success(`Transfer ${response.data.transfer_number} created`);
      onSuccess(response.data);
      onOpenChange(false);
      setFormData({
        from_location_id: '',
        to_location_id: '',
        transfer_type: 'INTERNAL',
        priority: 'NORMAL',
        reason: '',
        notes: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create transfer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5" />
            Create Transfer Request
          </DialogTitle>
          <DialogDescription>
            Move stock between locations with full traceability
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                From Location
                <ComplianceTooltip
                  clause="8.5.1"
                  title="Accountability"
                  description="Source location for stock transfer"
                />
              </Label>
              <Select
                value={formData.from_location_id}
                onValueChange={(v) => setFormData(prev => ({ ...prev, from_location_id: v }))}
              >
                <SelectTrigger data-testid="from-location-select">
                  <SelectValue placeholder="Select source..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>
                      {loc.code} ({loc.zone_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>To Location</Label>
              <Select
                value={formData.to_location_id}
                onValueChange={(v) => setFormData(prev => ({ ...prev, to_location_id: v }))}
              >
                <SelectTrigger data-testid="to-location-select">
                  <SelectValue placeholder="Select destination..." />
                </SelectTrigger>
                <SelectContent>
                  {locations.filter(l => l.location_id !== formData.from_location_id).map((loc) => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>
                      {loc.code} ({loc.zone_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Transfer Type</Label>
              <Select
                value={formData.transfer_type}
                onValueChange={(v) => setFormData(prev => ({ ...prev, transfer_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INTERNAL">Internal</SelectItem>
                  <SelectItem value="INTER_WAREHOUSE">Inter-Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Reason for Transfer</Label>
            <Input
              placeholder="e.g., Consolidation, Replenishment"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              data-testid="transfer-reason-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              placeholder="Additional notes..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="create-transfer-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Transfer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Issue Detail View Component
 */
const IssueDetailView = ({ issue, onRefresh }) => {
  const [showAddLine, setShowAddLine] = useState(false);
  const [showPickDialog, setShowPickDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  if (!issue) return null;

  const canAddLines = ['DRAFT', 'PICKING'].includes(issue.status);
  const canPick = ['DRAFT', 'PICKING'].includes(issue.status) && issue.line_count > 0;
  const canComplete = issue.status === 'PICKED' || (issue.status === 'PICKING' && issue.lines_picked > 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold">{issue.issue_number}</h3>
                <StatusBadge status={issue.status} type="issue" />
                <PriorityBadge priority={issue.priority} />
                {!issue.fifo_compliant && (
                  <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    FIFO Deviation
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span>{issue.demand_type?.replace(/_/g, ' ')}</span>
                {issue.demand_ref_number && <span>• Ref: {issue.demand_ref_number}</span>}
                <span>• {formatIST(issue.created_at)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              {canAddLines && (
                <Button size="sm" variant="outline" onClick={() => setShowAddLine(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Line
                </Button>
              )}
              {canPick && (
                <Button size="sm" onClick={() => setShowPickDialog(true)} data-testid="generate-picks-btn">
                  <ListOrdered className="w-4 h-4 mr-1" />
                  Generate Picks
                </Button>
              )}
              {canComplete && (
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setShowCompleteDialog(true)}>
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Complete
                </Button>
              )}
            </div>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-5 gap-4 mt-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-[#101957]">{issue.line_count}</p>
              <p className="text-xs text-gray-500">Lines</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-[#101957]">{issue.total_quantity_requested}</p>
              <p className="text-xs text-gray-500">Requested</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{issue.total_quantity_picked}</p>
              <p className="text-xs text-gray-500">Picked</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{issue.total_quantity_issued}</p>
              <p className="text-xs text-gray-500">Issued</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${issue.fifo_compliant ? 'text-green-600' : 'text-amber-600'}`}>
                {issue.fifo_deviations_count || 0}
              </p>
              <p className="text-xs text-gray-500">FIFO Deviations</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Lines Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Issue Lines</CardTitle>
        </CardHeader>
        <CardContent>
          {issue.lines?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">#</TableHead>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Requested</TableHead>
                  <TableHead className="text-right">Picked</TableHead>
                  <TableHead className="text-right">Issued</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>FIFO</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issue.lines.map((line) => (
                  <TableRow key={line.line_id}>
                    <TableCell>{line.line_number}</TableCell>
                    <TableCell className="font-medium">{line.part_number}</TableCell>
                    <TableCell className="text-gray-500 max-w-xs truncate">
                      {line.part_description}
                    </TableCell>
                    <TableCell className="text-right">{line.quantity_requested}</TableCell>
                    <TableCell className="text-right">{line.quantity_picked}</TableCell>
                    <TableCell className="text-right">{line.quantity_issued}</TableCell>
                    <TableCell>
                      <StatusBadge status={line.status} type="issue" />
                    </TableCell>
                    <TableCell>
                      {line.fifo_deviation ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="bg-amber-100 text-amber-700">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Deviation
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{line.fifo_deviation_reason || 'No reason provided'}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Badge variant="outline" className="bg-green-100 text-green-700">
                          <Check className="w-3 h-3 mr-1" />
                          Compliant
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No lines added yet</p>
              {canAddLines && (
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setShowAddLine(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add First Line
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialogs */}
      <AddIssueLineDialog
        open={showAddLine}
        onOpenChange={setShowAddLine}
        issueId={issue.issue_id}
        onSuccess={onRefresh}
      />
      <PickSuggestionsDialog
        open={showPickDialog}
        onOpenChange={setShowPickDialog}
        issue={issue}
        onSuccess={onRefresh}
      />
      <CompleteIssueDialog
        open={showCompleteDialog}
        onOpenChange={setShowCompleteDialog}
        issue={issue}
        onSuccess={onRefresh}
      />
    </div>
  );
};

/**
 * Main Issue/Transfer Page Component
 */
const IssueTransferPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'issues');
  
  // Issues state
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showCreateIssue, setShowCreateIssue] = useState(false);
  
  // Transfers state
  const [transfers, setTransfers] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(true);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showCreateTransfer, setShowCreateTransfer] = useState(false);
  
  // Filters
  const [issueStatusFilter, setIssueStatusFilter] = useState('');
  const [transferStatusFilter, setTransferStatusFilter] = useState('');
  
  // Fetch Issues
  const fetchIssues = useCallback(async () => {
    setIssuesLoading(true);
    try {
      const params = { page: 1, page_size: 50 };
      if (issueStatusFilter) params.status = issueStatusFilter;
      
      const response = await api.get('/api/v1/inventory/v2/issues', { params });
      setIssues(response.data.items || []);
    } catch (error) {
      console.error('Error fetching issues:', error);
      toast.error('Failed to load issues');
    } finally {
      setIssuesLoading(false);
    }
  }, [issueStatusFilter]);
  
  // Fetch Transfers
  const fetchTransfers = useCallback(async () => {
    setTransfersLoading(true);
    try {
      const params = { page: 1, page_size: 50 };
      if (transferStatusFilter) params.status = transferStatusFilter;
      
      const response = await api.get('/api/v1/inventory/v2/transfer-requests', { params });
      setTransfers(response.data.items || []);
    } catch (error) {
      console.error('Error fetching transfers:', error);
      toast.error('Failed to load transfers');
    } finally {
      setTransfersLoading(false);
    }
  }, [transferStatusFilter]);
  
  // Fetch issue details
  const fetchIssueDetails = async (issueId) => {
    try {
      const response = await api.get(`/api/v1/inventory/v2/issues/${issueId}`);
      setSelectedIssue(response.data);
    } catch (error) {
      console.error('Error fetching issue details:', error);
      toast.error('Failed to load issue details');
    }
  };
  
  // Fetch transfer details
  const fetchTransferDetails = async (transferId) => {
    try {
      const response = await api.get(`/api/v1/inventory/v2/transfer-requests/${transferId}`);
      setSelectedTransfer(response.data);
    } catch (error) {
      console.error('Error fetching transfer details:', error);
      toast.error('Failed to load transfer details');
    }
  };
  
  // Execute transfer
  const handleExecuteTransfer = async (transferId) => {
    try {
      await api.post(`/api/v1/inventory/v2/transfer-requests/${transferId}/execute`);
      toast.success('Transfer completed successfully');
      fetchTransfers();
      if (selectedTransfer?.transfer_id === transferId) {
        fetchTransferDetails(transferId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute transfer');
    }
  };
  
  useEffect(() => {
    if (activeTab === 'issues') {
      fetchIssues();
    } else {
      fetchTransfers();
    }
  }, [activeTab, fetchIssues, fetchTransfers]);
  
  const handleIssueCreated = (issue) => {
    fetchIssues();
    setSelectedIssue(issue);
    fetchIssueDetails(issue.issue_id);
  };
  
  const handleTransferCreated = (transfer) => {
    fetchTransfers();
    setSelectedTransfer(transfer);
    fetchTransferDetails(transfer.transfer_id);
  };
  
  const handleRefreshIssue = () => {
    if (selectedIssue) {
      fetchIssueDetails(selectedIssue.issue_id);
      fetchIssues();
    }
  };

  return (
    <InventoryLayout>
      <div className="min-h-screen" data-testid="issue-transfer-page">
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-[#101957]">Issue & Transfer</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Stock issue workflow with FIFO enforcement and location transfers
                </p>
              </div>
            </div>
          </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="px-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="issues" className="flex items-center gap-2" data-testid="issues-tab">
              <ArrowUpFromLine className="w-4 h-4" />
              Issues
              {issues.length > 0 && (
                <Badge variant="secondary" className="ml-1">{issues.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="transfers" className="flex items-center gap-2" data-testid="transfers-tab">
              <ArrowRightLeft className="w-4 h-4" />
              Transfers
              {transfers.length > 0 && (
                <Badge variant="secondary" className="ml-1">{transfers.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {/* Content */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Issues Tab */}
          <TabsContent value="issues" className="mt-0">
            <div className="grid grid-cols-12 gap-6">
              {/* Issues List */}
              <div className="col-span-4 space-y-4">
                <div className="flex gap-2">
                  <Select value={issueStatusFilter || "ALL"} onValueChange={(v) => setIssueStatusFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-[140px]" data-testid="issue-status-filter">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Status</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PICKING">Picking</SelectItem>
                      <SelectItem value="PICKED">Picked</SelectItem>
                      <SelectItem value="COMPLETED">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setShowCreateIssue(true)} data-testid="create-issue-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    New Issue
                  </Button>
                  <Button variant="outline" size="icon" onClick={fetchIssues}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {issuesLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                    </div>
                  ) : issues.length === 0 ? (
                    <Card className="p-8 text-center text-gray-500">
                      <ArrowUpFromLine className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                      <p>No issue orders found</p>
                    </Card>
                  ) : (
                    issues.map((issue) => (
                      <Card
                        key={issue.issue_id}
                        className={`p-3 cursor-pointer hover:border-blue-300 transition-colors ${
                          selectedIssue?.issue_id === issue.issue_id ? 'border-blue-500 bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          setSelectedIssue(issue);
                          fetchIssueDetails(issue.issue_id);
                        }}
                        data-testid={`issue-card-${issue.issue_number}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{issue.issue_number}</span>
                              <StatusBadge status={issue.status} type="issue" />
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {issue.demand_type?.replace(/_/g, ' ')}
                              {issue.demand_ref_number && ` - ${issue.demand_ref_number}`}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              <span>{issue.line_count} lines</span>
                              <span>Qty: {issue.total_quantity_requested}</span>
                              {!issue.fifo_compliant && (
                                <span className="text-amber-500">FIFO deviation</span>
                              )}
                            </div>
                          </div>
                          <PriorityBadge priority={issue.priority} />
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </div>
              
              {/* Issue Detail */}
              <div className="col-span-8">
                {selectedIssue ? (
                  <IssueDetailView issue={selectedIssue} onRefresh={handleRefreshIssue} />
                ) : (
                  <Card className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <ArrowUpFromLine className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <p>Select an issue order to view details</p>
                      <p className="text-sm mt-1">or create a new one</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Transfers Tab */}
          <TabsContent value="transfers" className="mt-0">
            <div className="grid grid-cols-12 gap-6">
              {/* Transfers List */}
              <div className="col-span-4 space-y-4">
                <div className="flex gap-2">
                  <Select value={transferStatusFilter || "ALL"} onValueChange={(v) => setTransferStatusFilter(v === "ALL" ? "" : v)}>
                    <SelectTrigger className="w-[140px]" data-testid="transfer-status-filter">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Status</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
                      <SelectItem value="COMPLETED">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => setShowCreateTransfer(true)} data-testid="create-transfer-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    New Transfer
                  </Button>
                  <Button variant="outline" size="icon" onClick={fetchTransfers}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                  {transfersLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                    </div>
                  ) : transfers.length === 0 ? (
                    <Card className="p-8 text-center text-gray-500">
                      <ArrowRightLeft className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                      <p>No transfer requests found</p>
                    </Card>
                  ) : (
                    transfers.map((transfer) => (
                      <Card
                        key={transfer.transfer_id}
                        className={`p-3 cursor-pointer hover:border-blue-300 transition-colors ${
                          selectedTransfer?.transfer_id === transfer.transfer_id ? 'border-blue-500 bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          setSelectedTransfer(transfer);
                          fetchTransferDetails(transfer.transfer_id);
                        }}
                        data-testid={`transfer-card-${transfer.transfer_number}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{transfer.transfer_number}</span>
                              <StatusBadge status={transfer.status} type="transfer" />
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                              <span>{transfer.from_location_code}</span>
                              <ChevronRight className="w-4 h-4" />
                              <span>{transfer.to_location_code}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                              <span>{transfer.line_count} lines</span>
                              <span>Qty: {transfer.total_quantity}</span>
                            </div>
                          </div>
                          <PriorityBadge priority={transfer.priority} />
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </div>
              
              {/* Transfer Detail */}
              <div className="col-span-8">
                {selectedTransfer ? (
                  <div className="space-y-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold">{selectedTransfer.transfer_number}</h3>
                              <StatusBadge status={selectedTransfer.status} type="transfer" />
                              <PriorityBadge priority={selectedTransfer.priority} />
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="font-mono">{selectedTransfer.from_location_code}</span>
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                              <span className="font-mono">{selectedTransfer.to_location_code}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">{formatIST(selectedTransfer.created_at)}</p>
                          </div>
                          {['DRAFT', 'PENDING'].includes(selectedTransfer.status) && selectedTransfer.line_count > 0 && (
                            <Button
                              onClick={() => handleExecuteTransfer(selectedTransfer.transfer_id)}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid="execute-transfer-btn"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Execute Transfer
                            </Button>
                          )}
                        </div>
                        
                        {/* Summary Stats */}
                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-[#101957]">{selectedTransfer.line_count}</p>
                            <p className="text-xs text-gray-500">Lines</p>
                          </div>
                          <div className="text-center">
                            <p className="text-2xl font-bold text-[#101957]">{selectedTransfer.total_quantity}</p>
                            <p className="text-xs text-gray-500">Total Qty</p>
                          </div>
                          <div className="text-center">
                            <Badge variant={selectedTransfer.is_inter_warehouse ? 'default' : 'secondary'}>
                              {selectedTransfer.is_inter_warehouse ? 'Inter-Warehouse' : 'Internal'}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Transfer Lines */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Transfer Lines</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedTransfer.lines?.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[60px]">#</TableHead>
                                <TableHead>Lot Number</TableHead>
                                <TableHead>Part Number</TableHead>
                                <TableHead className="text-right">Quantity</TableHead>
                                <TableHead className="text-right">Transferred</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedTransfer.lines.map((line) => (
                                <TableRow key={line.line_id}>
                                  <TableCell>{line.line_number}</TableCell>
                                  <TableCell className="font-mono">{line.lot_number}</TableCell>
                                  <TableCell className="font-medium">{line.part_number}</TableCell>
                                  <TableCell className="text-right">{line.quantity}</TableCell>
                                  <TableCell className="text-right">{line.quantity_transferred}</TableCell>
                                  <TableCell>
                                    <StatusBadge status={line.status} type="transfer" />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Boxes className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                            <p>No lines added yet</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <Card className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <ArrowRightLeft className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <p>Select a transfer request to view details</p>
                      <p className="text-sm mt-1">or create a new one</p>
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Dialogs */}
      <CreateIssueDialog
        open={showCreateIssue}
        onOpenChange={setShowCreateIssue}
        onSuccess={handleIssueCreated}
      />
      <CreateTransferDialog
        open={showCreateTransfer}
        onOpenChange={setShowCreateTransfer}
        onSuccess={handleTransferCreated}
      />
      </div>
    </InventoryLayout>
  );
};

export default IssueTransferPage;
